import { EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Component } from '@angular/core';

import { AppService } from '../../services/app.service';
import { ClassifierService } from '../../services/classifier.service';
import { IClassifier } from '../../models/Classifier';
import { LessonObservationService } from '../../services/lesson-observation.service';
import {
    ILessonObservationEvent, ILessonObservationTeacher, ILessonObservationStructure, ILessonObservationEditModel,
    ILessonObservation, ILessonObservationObserver
} from '../../models/LessonObservation';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LessonObservationEventSelectComponent } from './event-select.component';
import { tap } from 'rxjs/operators';
import { of } from 'rxjs';

const cache: {
    structures: { [upn: string]: ILessonObservationStructure[] },
    observers: { [upn: string]: ILessonObservationObserver[] },
} = {
    structures: {},
    observers: {}
};

@Component({
    selector: 'app-lesson-observation-edit',
    templateUrl: './edit.component.html'
})
export class LessonObservationEditComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: LessonObservationService,
        private classifiers: ClassifierService,
        private modal: NgbModal
    ) { }

    @Input() data?: ILessonObservation;

    @Output('close') closeEvent = new EventEmitter<boolean>();

    readonly maxLength = {
        otherRole: 100,
        formatNotes: 200,
        environment: 4000,
        content: 4000,
        process: 4000,
        performance: 4000,
        other: 4000,
        conclusion: 4000
    };

    readonly model: ILessonObservationEditModel = <any>{
        ObserverRoleIds: [],
        TeacherRoleIds: [],
        GoalIds: []
    };

    event: ILessonObservationEvent;
    roles: IClassifier[] = [];
    goals: IClassifier[] = [];
    teacherStructures: ILessonObservationStructure[] = [];
    observerStructures: ILessonObservationStructure[] = [];
    observer: ILessonObservationObserver;
    observers: ILessonObservationObserver[] = [];

    readonly observerRoleModel: { [id: string]: boolean } = {};
    readonly teacherRoleModel: { [id: string]: boolean } = {};

    isNew: boolean;
    canSubmit: boolean;

    readonly observerDisplayFn = (option: ILessonObservationObserver) => `${option.Name} ${option.Surname}`;
    readonly observerFilterFn = (option: ILessonObservationObserver, term: string) => `${option.Name} ${option.Surname}`.toLowerCase().includes(term.toLowerCase());
    readonly teacherDisplayFn = (option: ILessonObservationTeacher) => `${option.Name} ${option.Surname}`;

    get closeBehaviour() {
        return this.closeEvent.observers.filter(t => t !== undefined).length ? 'cancel' : 'close';
    }

    get showTeacherOtherRole() {
        return this.model.TeacherRoleIds.includes(this.otherRoleId);
    }

    get showObserverOtherRole() {
        return this.model.ObserverRoleIds.includes(this.otherRoleId);
    }

    private otherRoleId: string;

    ngOnInit() {
        if (this.data) {
            this.canSubmit = this.data.Status == 'Draft' || this.data.Status == 'Rejected';

            this.model.Conclusion = this.data.Conclusion;
            this.model.Content = this.data.Content;
            this.model.Environment = this.data.Environment;
            this.model.EventId = this.data.EventId;
            this.model.Format = this.data.Format;
            this.model.FormatNotes = this.data.FormatNotes;
            this.model.GoalIds = this.data.Goals.map(t => t.Id);
            this.model.ObserverOtherRole = this.data.ObserverOtherRole;
            this.model.ObserverRoleIds = this.data.ObserverRoles.map(t => t.Id);
            this.model.ObserverStructureId = this.data.ObserverStructureId;
            this.model.ObserverUpn = this.data.ObserverUpn;
            this.model.Other = this.data.Other;
            this.model.OtherGoal = this.data.OtherGoal;
            this.model.Performance = this.data.Performance;
            this.model.Process = this.data.Process;
            this.model.TeacherOtherRole = this.data.TeacherOtherRole;
            this.model.TeacherRoleIds = this.data.TeacherRoles.map(t => t.Id);
            this.model.TeacherStructureId = this.data.TeacherStructureId;
            this.model.TeacherUpn = this.data.TeacherUpn;

            this.event = {
                Date: this.data.Date,
                Id: this.data.EventId,
                ProgramCode: this.data.ProgramCode,
                ProgramId: this.data.ProgramCode,
                ProgramLevel: this.data.ProgramLevel,
                ProgramName: this.data.ProgramName,
                ProgramType: this.data.ProgramType,
                Location: this.data.Location,
                SubjectCode: this.data.SubjectCode,
                SubjectName: this.data.SubjectName,
                Teachers: [{
                    Name: this.data.TeacherName,
                    Surname: this.data.TeacherSurname,
                    Upn: this.data.TeacherUpn
                }],
                TimeFrom: this.data.TimeFrom,
                TimeTo: this.data.TimeTo,
                Type: this.data.Type,
                TypeId: this.data.TypeId
            };

            this.observer = {
                Name: this.data.ObserverName,
                Surname: this.data.ObserverSurname,
                Structure: this.data.ObserverStructure,
                StructureId: this.data.ObserverStructureId,
                Upn: this.data.ObserverUpn
            };

            this.observers = [this.observer];

            this.observerStructures = [{
                Id: this.data.ObserverStructureId,
                Name: this.data.ObserverStructure
            }];

            this.teacherStructures = [{
                Id: this.data.TeacherStructureId,
                Name: this.data.TeacherStructure
            }];

            this.model.ObserverRoleIds.forEach(t => {
                this.observerRoleModel[t] = true;
            });

            this.model.TeacherRoleIds.forEach(t => {
                this.teacherRoleModel[t] = true;
            });

            this.getObservers().subscribe(data => {
                this.observers.push(...data.filter(t => t.Upn != this.observer.Upn));
            });

            this.app.addLoading(this.getStructures(this.model.TeacherUpn)).subscribe(data => {
                this.teacherStructures.push(...data.filter(t => t.Id != this.data.TeacherStructureId));
            });
        } else {
            this.isNew = true;
            this.canSubmit = true;
            this.selectEvent();
        }

        this.app.addLoading(this.classifiers.get(['LessonObservationRole', 'LessonObservationGoal'])).subscribe(data => {
            const roles = data.filter(t => t.Type == 'LessonObservationRole');

            this.otherRoleId = roles.find(t => t.Code == 'Other')?.Id;

            roles.sort((a, b) => {
                return a.Id == this.otherRoleId ? 1 : -1;
            });

            roles.forEach(t => {
                if (this.observerRoleModel[t.Id] === undefined) this.observerRoleModel[t.Id] = false;
                if (this.teacherRoleModel[t.Id] === undefined) this.teacherRoleModel[t.Id] = false;
            });

            this.roles = roles;
            this.goals = data.filter(t => t.Type == 'LessonObservationGoal');
        });
    }

    onObserverChange() {
        this.model.ObserverUpn = this.observer.Upn;
        this.model.ObserverStructureId = this.observer.StructureId;
    }

    onTeacherChange() {
        this.model.TeacherOtherRole = undefined;
        this.model.TeacherRoleIds = [];

        this.getStructures(this.model.TeacherUpn).subscribe(data => {
            this.teacherStructures = data;

            if (data.length == 1) {
                this.model.TeacherStructureId = data[0].Id;
            }
        });
    }

    onGoalChange(goalId: string, checked: boolean) {
        const ix = this.model.GoalIds.indexOf(goalId);

        if (checked) {
            if (ix == -1) {
                this.model.GoalIds.push(goalId);
            }
        } else if (ix > -1) {
            this.model.GoalIds.splice(ix, 1);
        }
    }

    selectEvent() {
        const ref = this.modal.open(LessonObservationEventSelectComponent, { size: 'lg' });
        ref.result.then((event: ILessonObservationEvent) => {
            this.event = event;
            this.model.EventId = event.Id;

            if (event.Teachers.length == 1) {
                this.model.TeacherUpn = event.Teachers[0].Upn;
                this.onTeacherChange();
            }

            this.getObservers().subscribe(data => {
                this.observers = data;

                this.observer = data.length == 1 ? data[0] : data.find(t => t.Upn == this.app.currentUser.username);

                if (this.observer) {
                    this.onObserverChange();
                }
            });
        }, () => { });
    }

    save(isFinal: boolean = false) {
        const errors: string[] = [];

        this.model.IsFinal = isFinal;

        this.model.ObserverRoleIds = Object.keys(this.observerRoleModel).filter(t => this.observerRoleModel[t]);
        this.model.TeacherRoleIds = Object.keys(this.teacherRoleModel).filter(t => this.teacherRoleModel[t]);

        if (!this.model.ObserverRoleIds.length) {
            errors.push(this.app.translate('lessonObservation_observerRoleRequired'));
        }

        if (!this.model.TeacherRoleIds.length) {
            errors.push(this.app.translate('lessonObservation_teacherRoleRequired'));
        }

        if (errors.length) {
            this.app.showError(errors.join('\n'));
            return;
        }

        if (!this.showTeacherOtherRole) this.model.TeacherOtherRole = undefined;
        if (!this.showObserverOtherRole) this.model.ObserverOtherRole = undefined;

        const proceed = () => {
            const req = this.isNew
                ? this.service.create(this.model)
                : this.service.update(this.data.Id, this.model);

            this.app.addLoading(req).subscribe(id => {
                this.app.notify(isFinal ? 'lessonObservation_submitted' : 'lessonObservation_saved');
                this.smartClose(true);
            });
        };

        if (isFinal) {
            this.app.confirm({
                text: this.app.translate('lessonObservation_confirmSubmit'),
                okText: this.app.translate('submit')
            }, ok => {
                if (ok) proceed();
            });
        } else {
            proceed();
        }
    }

    close() {
        this.smartClose(false);
    }

    private smartClose(isSave: boolean) {
        if (this.model.IsFinal) {
            this.app.navigate(['/lesson-observations']);
        } else {
            if (this.closeBehaviour == 'cancel') {
                this.closeEvent.emit(isSave);
            } else {
                this.app.navigate(['/lesson-observations']);
            }
        }
    }

    private getStructures(upn: string) {
        if (cache.structures[upn]) {
            return of(cache.structures[upn]);
        }

        return this.app.addLoading(this.service.getStructures(upn)).pipe(tap(data => {
            cache.structures[upn] = data;
        }));
    }

    private getObservers() {
        const upn = this.app.currentUser.username;

        if (cache.observers[upn]) {
            return of(cache.observers[upn]);
        }

        return this.app.addLoading(this.service.getObservers()).pipe(tap(data => {
            cache.observers[upn] = data;
        }));
    }
}
